import { HttpErrorResponse } from '@angular/common/http';
import { catchError, map, Observable, of } from 'rxjs';

export type EnrichedResult<T> =
  | {
    success: true;
    data: T;
  }
  | ErrorResponse;

export type ErrorResponse = {
  success: false;
  statusCode: number;
  errors: WellKnownError[];
  response?: HttpErrorResponse;
};

export enum WKECodes {
  LaborCostNotSuitable = 'WKE:C:001',
  NoLaborCostRateForDates = 'WKE:C:002',
  RegisterCostNotEligibleEstimates = 'WKE:C:003',
  RegisterCostNotSuitableEstimates = 'WKE:C:004',
  CreateEstimatedCostNotDraftBudgetVersion = 'WKE:C:005',
  CreateEstimatedCostCannotOvercomeBudget = 'WKE:C:006',
  DeleteEstimatedCostNotDraftBudgetVersion = 'WKE:C:007',
  UserNotEnabledToLogHoursInTheProject = 'WKE:C:008',

  // Attachments
  ClaimDeletedAttachment = 'WKE:ATT:001',

  // Budget
  DisallowedMultiplebudgets = 'WKE:B:001',

  // Budget versions
  ActivateNotCurrentBudgetVersion = 'WKE:BV:001',
  ActivateNotDraftBudgetVersion = 'WKE:BV:002',
  SetCurrentNotDraftBudgetVersion = 'WKE:BV:003',
  UpdateBudgetVersionNotDraft = 'WKE:BV:004',
  UpdateBudgetVersionCannotOvercomeBudget = 'WKE:BV:005',
  UpdateBudgetVersionEstimatesOutOfRange = 'WKE:BV:006',
  UpdateBudgetVersionEstimatesEntirelyOutOfRange = 'WKE:BV:007',

  // Work package
  DeactivateWPStatusWhenDefault = 'WKE:WP:001',
  WPTypeMissingDefaultStatusExists = 'WKE:WP:002',
  WPWithATypeInARemovedStatusExists = 'WKE:WP:003',
  UpdateWorkPackagePositionDisallowedMove = 'WKE:WP:004',
  WPInvalidDynamicFormValue = 'WKE:WP:005',

  // Work package dependency
  WPDependencySelfReference = 'WKE:WPD:001',
  WPDependencyAlreadyExists = 'WKE:WPD:002',
  WPDependencyWouldCreateCycle = 'WKE:WPD:003',

  // WorkPackage Templates
  TemplateAlreadyExists = 'WKE:TE:001',

  // Unknown
  Unknown = 'Unknown',
}

export const translateWellKnownError = (error: WellKnownError) => {
  switch (error.error) {
    case WKECodes.LaborCostNotSuitable:
      return 'i18n.WellKnownError.laborCostNotSuitable';
    case WKECodes.NoLaborCostRateForDates:
      return 'i18n.WellKnownError.noLaborCostRateForDates';
    case WKECodes.RegisterCostNotEligibleEstimates:
      return 'i18n.WellKnownError.registerCostNotEligibleEstimates';
    case WKECodes.RegisterCostNotSuitableEstimates:
      return 'i18n.WellKnownError.registerCostNotSuitableEstimates';
    case WKECodes.CreateEstimatedCostNotDraftBudgetVersion:
      return 'i18n.WellKnownError.createEstimatedCostNotDraftBudgetVersion';
    case WKECodes.CreateEstimatedCostCannotOvercomeBudget:
      return 'i18n.WellKnownError.createEstimatedCostCannotOvercomeBudget';
    case WKECodes.DeleteEstimatedCostNotDraftBudgetVersion:
      return 'i18n.WellKnownError.deleteEstimatedCostNotDraftBudgetVersion';
    case WKECodes.UserNotEnabledToLogHoursInTheProject:
      return 'i18n.WellKnownError.userNotEnabledToLogHoursInTheProject';

    // Budget
    case WKECodes.DisallowedMultiplebudgets:
      return 'i18n.WellKnownError.disallowedMultiplebudgets';

    // Budget versions
    case WKECodes.ActivateNotCurrentBudgetVersion:
      return 'i18n.WellKnownError.activateNotCurrentBudgetVersion';
    case WKECodes.ActivateNotDraftBudgetVersion:
      return 'i18n.WellKnownError.activateNotDraftBudgetVersion';
    case WKECodes.SetCurrentNotDraftBudgetVersion:
      return 'i18n.WellKnownError.setCurrentNotDraftBudgetVersion';
    case WKECodes.UpdateBudgetVersionNotDraft:
      return 'i18n.WellKnownError.updateBudgetVersionNotDraft';
    case WKECodes.UpdateBudgetVersionCannotOvercomeBudget:
      return 'i18n.WellKnownError.updateBudgetVersionCannotOvercomeBudget';
    case WKECodes.UpdateBudgetVersionEstimatesOutOfRange:
      return 'i18n.WellKnownError.updateBudgetVersionEstimatesOutOfRange';
    case WKECodes.UpdateBudgetVersionEstimatesEntirelyOutOfRange:
      return 'i18n.WellKnownError.updateBudgetVersionEstimatesEntirelyOutOfRange';

    // Work package
    case WKECodes.DeactivateWPStatusWhenDefault:
      return 'i18n.WellKnownError.deactivateWPStatusWhenDefault';
    case WKECodes.WPTypeMissingDefaultStatusExists:
      return 'i18n.WellKnownError.wPTypeMissingDefaultStatusExists';
    case WKECodes.WPWithATypeInARemovedStatusExists:
      return 'i18n.WellKnownError.wPWithATypeInARemovedStatusExists';
    case WKECodes.UpdateWorkPackagePositionDisallowedMove:
      return 'i18n.WellKnownError.updateWorkPackagePositionDisallowedMove';
    case WKECodes.WPInvalidDynamicFormValue:
      return 'i18n.WellKnownError.wPInvalidDynamicFormValue';

    // Work package dependency
    case WKECodes.WPDependencySelfReference:
      return 'i18n.WellKnownError.wPDependencySelfReference';
    case WKECodes.WPDependencyAlreadyExists:
      return 'i18n.WellKnownError.wPDependencyAlreadyExists';
    case WKECodes.WPDependencyWouldCreateCycle:
      return 'i18n.WellKnownError.wPDependencyWouldCreateCycle';

    // Attachments
    case WKECodes.ClaimDeletedAttachment:
      return 'i18n.WellKnownError.claimDeletedAttachment';

    // WorkPackage Templates
    case WKECodes.TemplateAlreadyExists:
      return 'i18n.WellKnownError.templateAlreadyExists';

    case WKECodes.Unknown:
      return 'i18n.WellKnownError.unknown';
  }
};

export type WellKnownError = { error: WKECodes; code: string; message?: string };

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const parseError = (response: any): WellKnownError[] => {
  const errors: WellKnownError[] = [];
  const rawErrors = typeof response?.error === 'string' ? JSON.parse(response.error) : response.error;
  for (const [code, message] of Object.entries(rawErrors.errors ?? {})) {
    const wke = Object.values(WKECodes).find((c) => code === c);
    errors.push({
      error: (wke ?? WKECodes.Unknown) as WKECodes,
      code: code,
      message: typeof message === 'string' && message !== '' ? message : undefined,
    });
  }
  if (!errors.length) {
    errors.push({
      error: WKECodes.Unknown,
      code: WKECodes.Unknown,
      message: response.message,
    });
  }
  return errors;
};

export const hasError = ({ errors }: ErrorResponse, code: WKECodes) => errors.find((e) => e.code === code);

export const hasStatus = ({ statusCode }: ErrorResponse, status: number) => statusCode === status;

export function enrichResult<T>(): (source$: Observable<T>) => Observable<EnrichedResult<T>> {
  return (source$) =>
    source$.pipe(
      map((data) => ({ success: true, data }) as EnrichedResult<T>),
      catchError((response) =>
        of({
          success: false,
          errors: parseError(response),
          statusCode: response.status as number,
          response,
        } as ErrorResponse),
      ),
    );
}
